import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import {
  CheckCircle,
  XCircle,
  Loader,
  Clock,
  AlertCircle,
  Trophy,
  BookOpen,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import gamificationService from "../../services/gamificationService";
import toast from "react-hot-toast";

interface Question {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  chapter: number;
  difficulty: string;
}

const CourseTest: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading test...");
  const [courseData, setCourseData] = useState<any>(null);
  const [testData, setTestData] = useState<any>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: number;
  }>({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(20 * 60); // 20 minutes

  useEffect(() => {
    const generateTest = async () => {
      if (!courseId || !user) return;

      try {
        // Fetch course data
        const courseDoc = await getDoc(doc(db, "personalizedCourses", courseId));

        if (!courseDoc.exists()) {
          throw new Error("Course not found");
        }

        const data = courseDoc.data();
        setCourseData(data);

        // Generate test from course
        setLoadingMessage("Generating test questions from your course...");

        const auth = getAuth(firebaseApp);
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("Not authenticated");
        const idToken = await currentUser.getIdToken();

        const response = await fetch(
          "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateCourseTest",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              courseContent: data.courseData,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to generate test");
        }

        const result = await response.json();
        setTestData(result.test);
        setLoading(false);
      } catch (error) {
        alert("Failed to generate test. Please try again.");
        navigate(`/job-seeker/personalized-course/${courseId}`);
      }
    };

    generateTest();
  }, [courseId, user, db, navigate]);

  // Timer effect
  useEffect(() => {
    if (testStarted && !testCompleted && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleTestSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [testStarted, testCompleted, timeRemaining]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: answerIndex,
    });
  };

  const handleNext = () => {
    if (
      currentQuestionIndex < testData.questions.length - 1 &&
      selectedAnswers[currentQuestionIndex] !== undefined
    ) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleTestSubmit = async () => {
    if (testCompleted) return;

    setTestCompleted(true);
    setLoadingMessage("Evaluating your test...");
    setLoading(true);

    try {
      // Calculate score
      let correctAnswers = 0;
      testData.questions.forEach((question: Question, index: number) => {
        if (selectedAnswers[index] === question.correctAnswer) {
          correctAnswers++;
        }
      });

      const finalScore = Math.round((correctAnswers / 10) * 100);
      setScore(finalScore);
      const passed = finalScore >= 60;

      // Save test result
      await addDoc(collection(db, "courseTestResults"), {
        userId: user!.id,
        courseId: courseId,
        score: finalScore,
        correctAnswers: correctAnswers,
        totalQuestions: 10,
        passed: passed,
        answers: selectedAnswers,
        questions: testData.questions,
        completedAt: Timestamp.now(),
      });

      // Update course with test status
      await updateDoc(doc(db, "personalizedCourses", courseId!), {
        courseTestPassed: passed,
      });

      if (passed) {
        // Award 5 points for passing the course test
        try {
          await gamificationService.addXP(user!.id, 5);
          await gamificationService.addPoints(user!.id, 5);
          toast.success("🎉 +5 points for passing the course test!");
        } catch (error) {
        }

        // Update profile to mark course as completed
        // Note: Profile is already verified from the skills test if they passed it
        await updateDoc(doc(db, "userProfiles", user!.id), {
          courseCompleted: true,
          courseTestPassed: true,
          lastCourseCompletedDate: Timestamp.now(),
        });

        toast.success("🎓 Course completed! Great job on your learning journey!");
      } else {
        toast.error(
          "You didn't pass. Please review the course and try again!"
        );
      }

      setLoading(false);
    } catch (error) {
      toast.error("Failed to save test result");
      setLoading(false);
      setTestCompleted(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <Loader className="w-16 h-16 text-red-600 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {loadingMessage}
            </h2>
            <p className="text-gray-600">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (testCompleted) {
    const passed = score >= 60;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full">
          <div className="text-center">
            <div
              className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
                passed ? "bg-green-100" : "bg-red-100"
              }`}
            >
              {passed ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : (
                <XCircle className="w-12 h-12 text-red-600" />
              )}
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {passed ? "Congratulations! 🎉" : "Test Completed"}
            </h1>
            <p className="text-gray-600 mb-6">
              {passed
                ? "You passed the course test! Your learning paid off!"
                : "You didn't pass this time. Review the course and try again!"}
            </p>

            <div className="inline-block bg-gray-100 rounded-lg px-8 py-4 mb-6">
              <p className="text-gray-600 text-sm mb-1">Your Score</p>
              <p className="text-5xl font-bold text-gray-900">{score}%</p>
              <p className="text-gray-600 text-sm mt-2">
                {Math.round((score / 100) * 10)} / 10 correct
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                {passed
                  ? "✅ You earned 10 points for passing! If you also passed the skills test, you're now verified and can take HR tests!"
                  : "❌ You need at least 60% to pass. Please review the course material and try again."}
              </p>
            </div>

            <div className="flex justify-center space-x-4">
              {!passed && (
                <button
                  onClick={() =>
                    navigate(`/job-seeker/personalized-course/${courseId}`)
                  }
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Review Course</span>
                </button>
              )}
              <button
                onClick={() => navigate("/job-seeker")}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Go to Dashboard
              </button>
              {passed && (
                <button
                  onClick={() => navigate("/job-seeker/tests")}
                  className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  <Trophy className="w-5 h-5" />
                  <span>Browse HR Tests</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!testStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Course Completion Test
            </h1>
            <p className="text-gray-600 mb-6">
              Test your understanding of the course material with 10 questions
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Questions</p>
                <p className="text-2xl font-bold text-gray-900">10</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Time Limit</p>
                <p className="text-2xl font-bold text-gray-900">20 min</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Passing Score</p>
                <p className="text-2xl font-bold text-gray-900">60%</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5" />
                <div className="text-left">
                  <h3 className="font-semibold text-yellow-900 mb-2">
                    Important Notes:
                  </h3>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• You need 60% (6/10) to pass</li>
                    <li>• You can retake the test if you don't pass</li>
                    <li>• Passing earns you 10 points!</li>
                    <li>
                      • If you passed the skills test, passing this will verify
                      your profile
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              onClick={() => setTestStarted(true)}
              className="px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-lg font-semibold"
            >
              Start Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = testData.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === testData.questions.length - 1;
  const answeredCount = Object.keys(selectedAnswers).length;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Timer & Progress */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                Question {currentQuestionIndex + 1} of 10
              </span>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4" />
                <span>
                  {answeredCount} / 10 answered
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-red-600 font-semibold">
              <Clock className="w-5 h-5" />
              <span>{formatTime(timeRemaining)}</span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
            <div
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${((currentQuestionIndex + 1) / 10) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">
                Chapter {currentQuestion.chapter} •{" "}
                <span className="capitalize">{currentQuestion.difficulty}</span>
              </p>
              <h2 className="text-2xl font-bold text-gray-900">
                {currentQuestion.question}
              </h2>
            </div>
          </div>

          <div className="space-y-3">
            {currentQuestion.options.map((option: string, index: number) => {
              const isSelected = selectedAnswers[currentQuestionIndex] === index;

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? "border-red-600 bg-red-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? "border-red-600 bg-red-600"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="text-gray-900">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              currentQuestionIndex === 0
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Previous
          </button>

          {isLastQuestion ? (
            <button
              onClick={handleTestSubmit}
              disabled={answeredCount < 10}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                answeredCount < 10
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              {answeredCount < 10
                ? `Answer All Questions (${answeredCount}/10)`
                : "Submit Test"}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={selectedAnswers[currentQuestionIndex] === undefined}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                selectedAnswers[currentQuestionIndex] === undefined
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseTest;
